import { Button, CustomDialog } from '../../../components';
import upscaleIcon from '../../../assets/icons/upscale.svg';
import { useState } from 'react';
import { TResponseImg } from '../../../utils/types/agrocraftArtisan';
import {
  useOverrideImageMutation,
  useUpscaleImageMutation,
  useTrackImageDownloadMutation,
  otpService,
} from '../../../services/agrocraftArtisan';
import { useAppDispatch } from '../../../storeHook';
import { showAlert } from '../../../reduxActions/alert';
import './index.scss';
import {
  CUSTOM_EVENTS,
  appLogEvent,
} from '../../../utils/analytics';
import { Tooltip } from '@mui/material';
import { downloadFile } from '../../../utils/functions/common';
import { extractAgripixPath } from '../../../utils/functions/string';
import { store } from '../../../store';

type TProps = {
  img: TResponseImg;
  chatId: number;
  setIsScrollToLatest: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

export const ImageUpscale = ({
  img,
  chatId,
  setIsScrollToLatest,
}: TProps) => {
  const [open, setOpen] = useState(false);
  const [isOverride, setIsOverride] = useState(false);
  const dispatch = useAppDispatch();

  const [upscaleImage, { isLoading: isLoadingUpscale }] =
    useUpscaleImageMutation();
  const [overrideImage, { isLoading: isLoadingOverride }] =
    useOverrideImageMutation();
  const [trackImageDownload] =
    useTrackImageDownloadMutation();

  const { Content: content } = img;

  const overrideImageAPI = (
    original_image_path: string,
    override_image_path: string
  ) => {
    setIsScrollToLatest(false);
    overrideImage({
      chatId,
      data: {
        original_image_path,
        override_image_path,
      },
    })
      .unwrap()
      .then(() => {
        setOpen(false);
        dispatch(
          showAlert({
            content:
              'Image Upscaled and overwritten successfully',
          })
        );
      })
      .catch(() => {});
  };

  const chatContentId = img?.image_id || img?.Content?.image_id;

  const trackDownload = async () => {
    if (chatContentId === undefined || chatContentId === null)
      return;
    try {
      await trackImageDownload(chatContentId).unwrap();
    } catch (error) {
      console.error('Failed to track upscaled image download:', error);
    }
  };

  const downloadUpscaledImage = async (imageSrc: string) => {
    void trackDownload();
    try {
      if (imageSrc.startsWith('blob:')) {
        await downloadFile(imageSrc);
        return;
      }

      const agripixPath = extractAgripixPath(imageSrc);

      if (agripixPath) {
        const result = await store.dispatch(
          otpService.endpoints.getImageByLink.initiate(agripixPath)
        );

        if ('data' in result && result.data) {
          const pathParts = agripixPath.split('/');
          const filename = pathParts[pathParts.length - 1] || 'upscaled-image.png';
          await downloadFile('', filename, async () => result.data as Blob);
        } else {
          throw new Error('Failed to fetch image');
        }
      } else {
        await downloadFile(imageSrc);
      }
    } catch (error) {
      console.error('Error downloading upscaled image:', error);
      dispatch(
        showAlert({
          content: 'Failed to download upscaled image',
          severity: 'error',
        })
      );
    }
  };

  const onBtnClick = (overRide?: boolean) => {
    appLogEvent(CUSTOM_EVENTS.IMAGE_UPSCALE, {
      overRide,
    });
    setIsOverride(overRide ? true : false);
    upscaleImage({
      upscale_size: 2,
      image_path: content.image_link,
    })
      .unwrap()
      .then((res) => {
        const updatedImagePath = res.updated_image_path;
        if (updatedImagePath) {
          if (overRide) {
            overrideImageAPI(
              content.image_link,
              updatedImagePath
            );
          } else {
            setOpen(false);
            dispatch(
              showAlert({
                content: 'Image Upscaled successfully',
              })
            );
            downloadUpscaledImage(updatedImagePath);
          }
        } else {
          dispatch(
            showAlert({
              content:
                'Oops! An unexpected error occurred. Please try again later.',
              severity: 'error',
            })
          );
        }
      })
      .catch(() => {});
  };

  const isLoadingUpscaleAndOverride =
    isOverride && (isLoadingOverride || isLoadingUpscale);
  const isLoadingUpscaleAndDownload =
    !isOverride && isLoadingUpscale;
  const areBtnsDisabled =
    isLoadingOverride || isLoadingUpscale;

  return (
    <div>
      <Tooltip title="Upscale image resolution">
        <a
          onClick={() =>
            !content.is_image_overridden && setOpen(!open)
          }
          className={
            content.is_image_overridden ? 'disabled' : ''
          }>
          <img src={upscaleIcon} />
        </a>
      </Tooltip>
      <CustomDialog
        dialogTitle="Image Upscale"
        open={open}
        setOpen={setOpen}
        maxWidth="md"
        showActions={false}
        headerClass="popup-header">
        <section className="popup-content">
          <div className="content-box">
            <div>
              Current size: {content.width}x{content.height}
            </div>
            <div>Resolution: 72 dpi</div>
            <div className="disclaimer">
              The new image will be upscaled to 2x
            </div>
            <div>
              New size: {content.width * 2}x{content.height * 2}
            </div>
            <div>New Resolution: 300 dpi</div>
            <div className="flex justify-between my-4">
              <Button
                disabled={areBtnsDisabled}
                isLoading={isLoadingUpscaleAndOverride}
                onClick={() => onBtnClick(true)}
                variant="button--secondary">
                Upscale & Overwrite
              </Button>
              <Button
                disabled={areBtnsDisabled}
                isLoading={isLoadingUpscaleAndDownload}
                onClick={() => onBtnClick()}>
                Upscale & Download
              </Button>
            </div>
          </div>
        </section>
      </CustomDialog>
    </div>
  );
};
